import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Image, Button, Spinner, Alert, Badge, Row, Col, Modal } from 'react-bootstrap';

const GalleryPostPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);
  const [hasLiked, setHasLiked] = useState(false);
  const [showCopiedAlert, setShowCopiedAlert] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Red Color Scheme for Media
  const PRIMARY_RED = '#dc3545';
  const DARK_RED = '#a71e2a';
  const LIGHT_RED = '#e74c3c';
  const VERY_LIGHT_RED = '#fff5f5';
  const RED_TEXT = '#2c0b0e';
  const RED_BORDER = '#e74c3c';

  const translations = {
    'Back to Gallery': 'العودة إلى المعرض',
    'Published on': 'نشر في',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'Tags': 'الكلمات الدلالية',
    'Author': 'المؤلف',
    'Category': 'الفئة',
    'Error loading gallery': 'خطأ في تحميل المعرض',
    'Gallery not found': 'المعرض غير موجود',
    'Like': 'أعجبني',
    'Share': 'مشاركة',
    'Loading gallery...': 'جاري تحميل المعرض...',
    'You already liked this gallery': 'لقد أعجبت بهذا المعرض مسبقاً',
    'Thank you for your like!': 'شكراً لإعجابك!',
    'Liked': 'معجب به',
    'Link copied to clipboard!': 'تم نسخ الرابط إلى الحافظة!',
    'Field Photos': 'صور ميدانية',
    'Events': 'فعاليات',
    'Portraits': 'بورتريه',
    'Operations': 'عمليات',
    'Training': 'تدريب',
    'Images': 'الصور',
    'of': 'من'
  };

  const translateText = (text) => {
    if (text === gallery?.description) {
      return text;
    }
    return isArabic ? (translations[text] || text) : text;
  };

  const fontStyles = {
    arabic: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.8'
    },
    english: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.6'
    }
  };

  const currentFontStyle = isArabic ? fontStyles.arabic : fontStyles.english;

  const checkIfLiked = (galleryToken) => {
    const likedGalleries = JSON.parse(localStorage.getItem('likedGalleries') || '{}');
    return likedGalleries[galleryToken] === true;
  };

  const incrementViewCount = async (galleryId) => {
    try {
      const galleryRef = doc(db1, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        views: increment(1),
        lastViewed: serverTimestamp()
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (document.activeElement) {
        document.activeElement.blur();
      }
      
      await navigator.clipboard.writeText(text);
      setShowCopiedAlert(true);
      
      setTimeout(() => {
        setShowCopiedAlert(false);
      }, 2000);
      
    } catch (err) {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setShowCopiedAlert(true);
        setTimeout(() => setShowCopiedAlert(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
        alert('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!token) {
          setError('No token provided');
          setLoading(false);
          return;
        }

        const galleryQuery = query(
          collection(db1, 'galleries'),
          where('token', '==', token)
        );

        const querySnapshot = await getDocs(galleryQuery);
        
        if (!querySnapshot.empty) {
          const galleryDoc = querySnapshot.docs[0];
          const galleryData = galleryDoc.data();

          const galleryWithData = {
            id: galleryDoc.id,
            ...galleryData,
            createdAt: galleryData.createdAt?.toDate() || new Date(),
            tags: Array.isArray(galleryData.tags) ? galleryData.tags : [],
            images: Array.isArray(galleryData.images) ? galleryData.images : [],
            views: galleryData.views || 0,
            likes: galleryData.likes || 0
          };

          setGallery(galleryWithData);
          setHasLiked(checkIfLiked(token));

          await incrementViewCount(galleryDoc.id);

        } else {
          setError('Gallery not found');
        }

      } catch (err) {
        console.error('Error fetching gallery:', err);
        setError(`Error loading gallery: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [token]);

  const handleLike = async () => {
    if (!gallery) return;

    if (hasLiked) {
      alert(translateText('You already liked this gallery'));
      return;
    }

    try {
      await updateDoc(doc(db1, 'galleries', gallery.id), {
        likes: increment(1),
        lastLiked: serverTimestamp()
      });

      setGallery(prev => ({
        ...prev,
        likes: (prev.likes || 0) + 1
      }));
      setHasLiked(true);

      const likedGalleries = JSON.parse(localStorage.getItem('likedGalleries') || '{}');
      likedGalleries[gallery.token] = true;
      localStorage.setItem('likedGalleries', JSON.stringify(likedGalleries));

      alert(translateText('Thank you for your like!'));

    } catch (err) {
      console.error('Error updating likes:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryBadge = (category) => {
    const categories = {
      'field_photos': { text: translateText('Field Photos'), color: PRIMARY_RED },
      'events': { text: translateText('Events'), color: '#28a745' },
      'portraits': { text: translateText('Portraits'), color: '#007bff' },
      'operations': { text: translateText('Operations'), color: '#6f42c1' },
      'training': { text: translateText('Training'), color: '#fd7e14' }
    };
    return categories[category] || { text: category, color: PRIMARY_RED };
  };

  const openImageModal = (image, index) => {
    setSelectedImage({ ...image, index });
    setShowImageModal(true);
  };

  const navigateImage = (direction) => {
    if (!gallery || !gallery.images || !selectedImage) return;

    const currentIndex = selectedImage.index;
    let newIndex;

    if (direction === 'next') {
      newIndex = (currentIndex + 1) % gallery.images.length;
    } else {
      newIndex = (currentIndex - 1 + gallery.images.length) % gallery.images.length;
    }

    setSelectedImage({ ...gallery.images[newIndex], index: newIndex });
  };

  if (loading) {
    return (
      <Container className="my-5 text-center" style={currentFontStyle}>
        <Spinner 
          animation="border" 
          style={{ 
            color: PRIMARY_RED,
            width: '3rem', 
            height: '3rem' 
          }} 
        />
        <p className="mt-3" style={{ color: PRIMARY_RED, fontSize: '1.1rem' }}>
          {translateText('Loading gallery...')}
        </p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5" style={currentFontStyle}>
        <Alert variant="danger">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        </Alert>
        <Button 
          variant="primary" 
          onClick={() => navigate('/gallery')}
          style={{ 
            backgroundColor: PRIMARY_RED, 
            borderColor: PRIMARY_RED,
            borderRadius: '8px'
          }}
        >
          {translateText('Back to Gallery')}
        </Button>
      </Container>
    );
  }

  if (!gallery) {
    return (
      <Container className="my-5" style={currentFontStyle}>
        <Alert variant="warning">
          {translateText('Gallery not found')}
        </Alert>
        <Button 
          variant="primary" 
          onClick={() => navigate('/gallery')}
          style={{ 
            backgroundColor: PRIMARY_RED, 
            borderColor: PRIMARY_RED,
            borderRadius: '8px'
          }}
        >
          {translateText('Back to Gallery')}
        </Button>
      </Container>
    );
  }

  const categoryBadge = getCategoryBadge(gallery.category);

  return (
    <Container className="my-5" style={{ 
      maxWidth: '1200px',
      direction: isArabic ? 'rtl' : 'ltr',
      ...currentFontStyle
    }}>
      {/* Fullscreen Image Modal */}
      <Modal 
        show={showImageModal} 
        onHide={() => setShowImageModal(false)} 
        size="xl" 
        centered
        style={{ zIndex: 9999 }}
      >
        <Modal.Body className="p-0 bg-dark position-relative">
          {selectedImage && (
            <>
              <Image 
                src={selectedImage.url} 
                style={{ 
                  width: '100%', 
                  height: '90vh', 
                  objectFit: 'contain' 
                }}
                alt={selectedImage.caption || gallery.title}
              />
              
              {/* Navigation Buttons */}
              {gallery.images.length > 1 && (
                <>
                  <Button 
                    variant="light"
                    className="position-absolute top-50 start-0 translate-middle-y m-3 rounded-circle"
                    onClick={() => navigateImage('prev')}
                    style={{
                      width: '50px',
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      border: 'none'
                    }}
                  >
                    <i className="bi bi-chevron-left" style={{ color: PRIMARY_RED }}></i>
                  </Button>
                  <Button 
                    variant="light"
                    className="position-absolute top-50 end-0 translate-middle-y m-3 rounded-circle"
                    onClick={() => navigateImage('next')}
                    style={{
                      width: '50px',
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      border: 'none'
                    }}
                  >
                    <i className="bi bi-chevron-right" style={{ color: PRIMARY_RED }}></i>
                  </Button>
                </>
              )}
              
              {/* Image Counter */}
              <div className="position-absolute top-0 start-50 translate-middle-x mt-3">
                <Badge 
                  style={{ 
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    fontSize: '1rem',
                    padding: '8px 16px',
                    fontWeight: '600'
                  }}
                >
                  {selectedImage.index + 1} {translateText('of')} {gallery.images.length}
                </Badge>
              </div>
              
              {/* Close Button */}
              <Button 
                variant="light"
                className="position-absolute top-0 end-0 m-3 rounded-circle"
                onClick={() => setShowImageModal(false)}
                style={{
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  border: 'none'
                }}
              >
                <i className="bi bi-x-lg" style={{ color: PRIMARY_RED }}></i>
              </Button>
              
              {/* Image Caption */}
              {selectedImage.caption && (
                <div className="position-absolute bottom-0 start-0 end-0 p-3 text-center">
                  <div 
                    style={{ 
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      display: 'inline-block',
                      maxWidth: '80%'
                    }}
                  >
                    {selectedImage.caption}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Copied Alert */}
      {showCopiedAlert && (
        <Alert 
          variant="success" 
          className="position-fixed top-0 start-50 translate-middle-x mt-3"
          style={{ zIndex: 9999, minWidth: '300px' }}
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-2"></i>
            {translateText('Link copied to clipboard!')}
          </div>
        </Alert>
      )}

      {/* Navigation */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button 
          variant="outline-primary"
          onClick={() => navigate('/gallery')}
          style={{
            borderColor: PRIMARY_RED,
            color: PRIMARY_RED,
            borderRadius: '20px',
            fontWeight: '600'
          }}
        >
          <i className="bi bi-arrow-left me-2"></i>
          {translateText('Back to Gallery')}
        </Button>
        <Button 
          onClick={() => setIsArabic(!isArabic)}
          variant="outline-primary"
          size="sm"
          style={{
            borderColor: PRIMARY_RED,
            color: PRIMARY_RED,
            borderRadius: '20px',
            fontWeight: '600'
          }}
        >
          {isArabic ? 'English' : 'العربية'}
        </Button>
      </div>

      <Card className="shadow-lg" style={{ 
        border: 'none',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: VERY_LIGHT_RED
      }}>
        <Card.Body style={{ padding: '40px' }}>
          {/* Meta Information */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
            <Badge 
              style={{ 
                backgroundColor: categoryBadge.color, 
                color: 'white',
                fontSize: '0.9rem',
                padding: '8px 16px',
                fontWeight: '600'
              }}
            >
              {categoryBadge.text}
            </Badge>
            <div style={{ color: DARK_RED, fontSize: '0.9rem', fontWeight: '600' }}>
              <i className="bi bi-clock me-1"></i>
              {formatDate(gallery.createdAt)}
            </div>
          </div>

          {/* Title */}
          <h1 style={{ 
            color: DARK_RED, 
            fontWeight: '700',
            fontSize: '2.5rem',
            lineHeight: '1.2',
            marginBottom: '1rem'
          }}>
            {gallery.title || 'Untitled Gallery'}
          </h1>

          {/* Description */}
          {gallery.description && (
            <p style={{ 
              color: PRIMARY_RED,
              fontSize: '1.2rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              fontStyle: 'italic',
              fontWeight: '500'
            }}>
              {gallery.description}
            </p>
          )}

          {/* Author and Stats */}
          <Row className="align-items-center mb-4">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  borderRadius: '50%',
                  backgroundColor: PRIMARY_RED,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  marginRight: isArabic ? '0' : '15px',
                  marginLeft: isArabic ? '15px' : '0'
                }}>
                  {gallery.author?.charAt(0) || 'A'}
                </div>
                <div>
                  <div style={{ fontWeight: '700', color: DARK_RED, fontSize: '1.1rem' }}>
                    {gallery.author || 'Al-Majd Media'}
                  </div>
                  <small style={{ color: PRIMARY_RED, opacity: '0.8', fontWeight: '500' }}>
                    {translateText('Author')}
                  </small>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end gap-4" style={{ color: DARK_RED }}>
                <div className="text-center">
                  <div style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                    {gallery.views || 0}
                  </div>
                  <small style={{ opacity: '0.8', fontWeight: '500' }}>{translateText('Views')}</small>
                </div>
                <div className="text-center">
                  <div style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                    {gallery.likes || 0}
                  </div>
                  <small style={{ opacity: '0.8', fontWeight: '500' }}>{translateText('Likes')}</small>
                </div>
                <div className="text-center">
                  <div style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                    {gallery.images?.length || 0}
                  </div>
                  <small style={{ opacity: '0.8', fontWeight: '500' }}>{translateText('Images')}</small>
                </div>
              </div>
            </Col>
          </Row>

          {/* Gallery Images */}
          {gallery.images && gallery.images.length > 0 && (
            <div className="mb-4">
              <h3 style={{ color: DARK_RED, marginBottom: '2rem', fontWeight: '700', textAlign: 'center' }}>
                {translateText('Gallery Images')} ({gallery.images.length})
              </h3>
              <Row>
                {gallery.images.map((image, index) => (
                  <Col key={index} lg={4} md={6} className="mb-4">
                    <Card 
                      className="h-100 shadow-sm" 
                      style={{ 
                        border: 'none',
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => openImageModal(image, index)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(220, 53, 69, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div className="position-relative">
                        <Image 
                          src={image.url} 
                          style={{ 
                            height: '250px', 
                            width: '100%',
                            objectFit: 'cover',
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '12px'
                          }}
                          alt={image.caption || `Image ${index + 1}`}
                        />
                        <div 
                          className="position-absolute top-50 start-50 translate-middle"
                          style={{
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          }}
                        >
                          <i 
                            className="bi bi-zoom-in" 
                            style={{ 
                              fontSize: '2rem', 
                              color: 'white',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                            }}
                          ></i>
                        </div>
                      </div>
                      {image.caption && (
                        <Card.Body>
                          <p style={{ 
                            color: RED_TEXT,
                            fontSize: '0.9rem',
                            margin: 0,
                            fontWeight: '500'
                          }}>
                            {image.caption}
                          </p>
                        </Card.Body>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Tags */}
          {gallery.tags && gallery.tags.length > 0 && (
            <div className="mb-4">
              <h6 style={{ color: DARK_RED, marginBottom: '0.5rem', fontWeight: '700' }}>
                {translateText('Tags')}:
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {gallery.tags.map((tag, index) => (
                  <Badge 
                    key={index}
                    style={{ 
                      backgroundColor: 'white',
                      color: PRIMARY_RED,
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: `2px solid ${RED_BORDER}`,
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex gap-3 pt-4 border-top" style={{ borderColor: RED_BORDER + '!important' }}>
            <Button 
              variant={hasLiked ? "success" : "outline-primary"}
              onClick={handleLike}
              disabled={hasLiked}
              className="d-flex align-items-center"
              style={{ 
                borderRadius: '8px',
                fontWeight: '600',
                ...(hasLiked ? {
                  backgroundColor: LIGHT_RED,
                  borderColor: LIGHT_RED
                } : {
                  color: PRIMARY_RED,
                  borderColor: PRIMARY_RED,
                  backgroundColor: 'transparent'
                })
              }}
            >
              <i className={`bi ${hasLiked ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'} me-2`}></i>
              {hasLiked ? translateText('Liked') : translateText('Like')} ({gallery.likes || 0})
            </Button>
            <Button 
              variant="outline-primary"
              onClick={() => copyToClipboard(window.location.href)}
              className="d-flex align-items-center"
              style={{ 
                borderRadius: '8px',
                color: PRIMARY_RED,
                borderColor: PRIMARY_RED,
                backgroundColor: 'transparent',
                fontWeight: '600'
              }}
            >
              <i className="bi bi-share me-2"></i>
              {translateText('Share')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default GalleryPostPage;
